import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';
import { isExecutionPlanAllowed } from '../../../config/proposalScheduleAccess';
import SharePointBrowser from '../GaDashboard/SharePointBrowser';

const ACCENT = '#0f766e';
const API = (job) => `/execution-plans/${job}`;
const ATTACH_ACCEPT = /\.(pdf|docx?|xlsx?|csv|txt|png|jpe?g|webp)$/i;

const btn = {
  padding: '7px 12px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  color: '#334155',
};
const btnPrimary = { ...btn, background: ACCENT, borderColor: ACCENT, color: '#fff', fontWeight: 600 };

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function emptyBody() {
  return {
    overview: '',
    craneStand: { location: '', notes: '', p6Activities: [] },
    laydownArea: { location: '', notes: '', p6Activities: [] },
    preassemblyAreas: [],
    sequence: [],
    logistics: '',
    risks: [],
    notes: '',
  };
}

const ExecutionPlan = ({ jobNumber, jobName }) => {
  const { instance, accounts } = useMsal();
  const allowed = isExecutionPlanAllowed(accounts[0]?.username);
  const fileRef = useRef(null);
  const chatEndRef = useRef(null);

  const [planDate, setPlanDate] = useState(todayIso());
  const [dates, setDates] = useState([]);
  const [plan, setPlan] = useState(null);
  const [body, setBody] = useState(emptyBody());
  const [title, setTitle] = useState('');
  const [p6, setP6] = useState(null);
  const [rev, setRev] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showSp, setShowSp] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [members, setMembers] = useState([]);
  const [emailTo, setEmailTo] = useState([]);
  const [emailCc, setEmailCc] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3200);
  };

  const applyPlan = (data) => {
    setPlan(data);
    setBody(data?.body || emptyBody());
    setTitle(data?.title || `Execution Plan — ${data?.planDate || planDate}`);
    if (data?.p6ProjId) setRev(String(data.p6ProjId));
  };

  const loadDates = useCallback(async () => {
    if (!jobNumber) return;
    const res = await authenticatedApiCall(instance, accounts, 'GET', `${API(jobNumber)}/plans`);
    setDates(res.data?.data?.plans || []);
  }, [instance, accounts, jobNumber]);

  const loadPlan = useCallback(async (date) => {
    if (!jobNumber || !date) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${API(jobNumber)}/plans/${date}`);
      applyPlan(res.data.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load execution plan');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, jobNumber]);

  const loadP6 = useCallback(async (revId) => {
    if (!jobNumber) return;
    try {
      const qs = revId ? `?rev=${encodeURIComponent(revId)}` : '';
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${API(jobNumber)}/p6${qs}`);
      setP6(res.data.data);
      if (res.data.data?.selectedRev && !revId) setRev(String(res.data.data.selectedRev));
    } catch (e) {
      setP6({ available: false, reason: e.response?.data?.error || e.message, buckets: {} });
    }
  }, [instance, accounts, jobNumber]);

  useEffect(() => {
    if (!allowed || !jobNumber) return undefined;
    loadDates().catch(() => {});
    loadPlan(planDate);
    loadP6();
    return undefined;
  }, [allowed, jobNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [plan?.messages, chatBusy]);

  const save = async (status) => {
    try {
      setSaving(true);
      const res = await authenticatedApiCall(
        instance, accounts, 'PUT',
        `${API(jobNumber)}/plans/${planDate}`,
        {
          title,
          status: status || plan?.status || 'draft',
          body,
          p6ProjId: rev || undefined,
          p6ProjShortName: p6?.revision?.projShortName,
        }
      );
      applyPlan(res.data.data);
      await loadDates();
      showToast('Saved');
    } catch (e) {
      showToast(e.response?.data?.error || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const changeDate = async (next) => {
    if (!next) return;
    setPlanDate(next);
    await loadPlan(next);
  };

  const sendChat = async (text) => {
    const message = String(text || chatInput).trim();
    if (!message || chatBusy) return;
    setChatInput('');
    setChatBusy(true);
    try {
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `${API(jobNumber)}/plans/${planDate}/chat`,
        { message, rev },
        { timeout: 120000 }
      );
      applyPlan(res.data.data.plan);
      if (res.data.data.p6) setP6(res.data.data.p6);
    } catch (e) {
      showToast(e.response?.data?.error || 'Chat failed', 'error');
    } finally {
      setChatBusy(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await authenticatedApiCall(
        instance, accounts, 'POST',
        `${API(jobNumber)}/plans/${planDate}/attachments`,
        fd
      );
      await loadPlan(planDate);
      showToast(`Attached ${file.name}`);
    } catch (e) {
      showToast(e.response?.data?.error || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const pickSharePoint = async (pick) => {
    setShowSp(false);
    setUploading(true);
    try {
      await authenticatedApiCall(
        instance, accounts, 'POST',
        `${API(jobNumber)}/plans/${planDate}/attachments/sharepoint`,
        pick
      );
      await loadPlan(planDate);
      showToast(`Attached ${pick.name} from SharePoint`);
    } catch (e) {
      showToast(e.response?.data?.error || 'SharePoint attach failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const removeAtt = async (id) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      await authenticatedApiCall(
        instance, accounts, 'DELETE',
        `${API(jobNumber)}/plans/${planDate}/attachments/${id}`
      );
      await loadPlan(planDate);
    } catch (e) {
      showToast(e.response?.data?.error || 'Remove failed', 'error');
    }
  };

  const downloadPdf = async () => {
    try {
      await save();
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `${API(jobNumber)}/plans/${planDate}/pdf`,
        null,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const name = res.headers?.['content-disposition']?.match(/filename="?([^"]+)"?/)?.[1]
        || `${jobNumber}_Execution_Plan_${planDate}.pdf`;
      a.href = url;
      a.download = decodeURIComponent(name);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast(e.response?.data?.error || 'Download failed', 'error');
    }
  };

  const openEmail = async () => {
    setShowEmail(true);
    setEmailResult(null);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${API(jobNumber)}/email-recipients`);
      setMembers(res.data.data.members || []);
      setEmailTo(res.data.data.defaults?.to || []);
      setEmailCc(res.data.data.defaults?.cc || []);
      setEmailSubject(`LGL Execution Plan — ${jobNumber} — ${planDate}`);
    } catch (e) {
      showToast(e.response?.data?.error || 'Could not load recipients', 'error');
    }
  };

  const sendEmailNow = async () => {
    setEmailSending(true);
    setEmailResult(null);
    try {
      await save();
      const res = await authenticatedApiCall(
        instance, accounts, 'POST',
        `${API(jobNumber)}/plans/${planDate}/email`,
        { to: emailTo, cc: emailCc, subject: emailSubject }
      );
      setEmailResult(res.data.data);
      showToast('Email sent');
    } catch (e) {
      showToast(e.response?.data?.error || 'Email failed', 'error');
    } finally {
      setEmailSending(false);
    }
  };

  const patchArea = (key, field, value) => {
    setBody((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  if (!allowed) {
    return (
      <div style={{ padding: 32, color: '#64748b' }}>
        Execution Plan is limited to Project Controls (Yousef and Behzad).
      </div>
    );
  }

  const buckets = p6?.buckets || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: '#f8fafc' }}>
      <div style={{
        padding: '12px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontWeight: 700, color: '#0f172a' }}>Execution Plan</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{jobNumber}{jobName ? ` · ${jobName}` : ''}</div>
        </div>
        <input
          type="date"
          value={planDate}
          onChange={(e) => changeDate(e.target.value)}
          style={{ ...btn, padding: '6px 8px' }}
        />
        {dates.length > 0 && (
          <select value={planDate} onChange={(e) => changeDate(e.target.value)} style={btn}>
            {dates.map((d) => (
              <option key={d.planDate} value={d.planDate}>
                {d.planDate}{d.status === 'published' ? ' · published' : ''}
              </option>
            ))}
          </select>
        )}
        {p6?.available ? (
          <select
            value={rev}
            onChange={(e) => { setRev(e.target.value); loadP6(e.target.value); }}
            style={{ ...btn, minWidth: 220 }}
            title="P6 XER revisions — Current plus every dated copy, same catalog as GA Dashboards"
          >
            {(p6.revisions || []).map((r) => (
              <option key={r.id} value={r.id}>{r.label || r.projShortName}</option>
            ))}
          </select>
        ) : (
          <button type="button" style={{ ...btn, color: '#b45309' }} onClick={() => loadP6(rev)} title={p6?.reason || 'P6 offline'}>
            Retry P6
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button type="button" style={btn} onClick={() => setShowHelp(true)}>❓ Help</button>
        <ListenButton compact guideId="execution-plan-help" title="Execution Plan Help" accent={ACCENT} />
        <button type="button" style={btn} onClick={() => save()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button type="button" style={btn} onClick={() => save('published')} disabled={saving}>Publish date</button>
        <button type="button" style={btn} onClick={downloadPdf}>Download PDF</button>
        <button type="button" style={btnPrimary} onClick={openEmail}>Email</button>
      </div>

      {toast && (
        <div style={{
          margin: '8px 16px 0', padding: '8px 12px', borderRadius: 8,
          background: toast.type === 'error' ? '#fef2f2' : '#ecfdf5',
          color: toast.type === 'error' ? '#b91c1c' : '#047857', fontSize: 13,
        }}>{toast.message}</div>
      )}
      {error && <div style={{ margin: '8px 16px 0', color: '#b91c1c' }}>{error}</div>}

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) 340px', gap: 12, padding: 12 }}>
        <div style={{ overflow: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 }}>
          {loading ? (
            <div style={{ color: '#64748b' }}>Loading…</div>
          ) : (
            <div id="execution-plan-onepager">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{ width: '100%', border: 'none', fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}
              />
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
                One-pager · {planDate}
                {p6?.revision?.projShortName ? ` · ${p6.revision.projShortName}` : ''}
              </div>

              <Section title="Overview">
                <textarea
                  value={body.overview}
                  onChange={(e) => setBody((p) => ({ ...p, overview: e.target.value }))}
                  rows={3}
                  placeholder="How this lift / install day is supposed to go…"
                  style={ta}
                />
              </Section>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Section title="Crane stand">
                  <input value={body.craneStand.location} onChange={(e) => patchArea('craneStand', 'location', e.target.value)} placeholder="Where the crane stands" style={inp} />
                  <textarea value={body.craneStand.notes} onChange={(e) => patchArea('craneStand', 'notes', e.target.value)} rows={3} placeholder="Pad, boom, pick radius…" style={ta} />
                </Section>
                <Section title="Laydown area">
                  <input value={body.laydownArea.location} onChange={(e) => patchArea('laydownArea', 'location', e.target.value)} placeholder="Yard / laydown" style={inp} />
                  <textarea value={body.laydownArea.notes} onChange={(e) => patchArea('laydownArea', 'notes', e.target.value)} rows={3} placeholder="What sits there, access…" style={ta} />
                </Section>
              </div>

              <Section title="Preassembly areas">
                {(body.preassemblyAreas || []).map((a, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 6, marginBottom: 6 }}>
                    <input value={a.name} onChange={(e) => setBody((p) => ({ ...p, preassemblyAreas: p.preassemblyAreas.map((x, j) => j === i ? { ...x, name: e.target.value } : x) }))} placeholder="Name" style={inp} />
                    <input value={a.location} onChange={(e) => setBody((p) => ({ ...p, preassemblyAreas: p.preassemblyAreas.map((x, j) => j === i ? { ...x, location: e.target.value } : x) }))} placeholder="Location" style={inp} />
                    <input value={a.notes} onChange={(e) => setBody((p) => ({ ...p, preassemblyAreas: p.preassemblyAreas.map((x, j) => j === i ? { ...x, notes: e.target.value } : x) }))} placeholder="Notes" style={inp} />
                    <button type="button" style={btn} onClick={() => setBody((p) => ({ ...p, preassemblyAreas: p.preassemblyAreas.filter((_, j) => j !== i) }))}>×</button>
                  </div>
                ))}
                <button type="button" style={btn} onClick={() => setBody((p) => ({ ...p, preassemblyAreas: [...p.preassemblyAreas, { name: '', location: '', notes: '', p6Activities: [] }] }))}>+ Area</button>
              </Section>

              <Section title="Sequence">
                {(body.sequence || []).map((s, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '40px 1fr 1.4fr auto', gap: 6, marginBottom: 6 }}>
                    <input value={s.step} onChange={(e) => setBody((p) => ({ ...p, sequence: p.sequence.map((x, j) => j === i ? { ...x, step: e.target.value } : x) }))} style={inp} />
                    <input value={s.title} onChange={(e) => setBody((p) => ({ ...p, sequence: p.sequence.map((x, j) => j === i ? { ...x, title: e.target.value } : x) }))} placeholder="Step" style={inp} />
                    <input value={s.detail} onChange={(e) => setBody((p) => ({ ...p, sequence: p.sequence.map((x, j) => j === i ? { ...x, detail: e.target.value } : x) }))} placeholder="Detail / dates" style={inp} />
                    <button type="button" style={btn} onClick={() => setBody((p) => ({ ...p, sequence: p.sequence.filter((_, j) => j !== i) }))}>×</button>
                  </div>
                ))}
                <button type="button" style={btn} onClick={() => setBody((p) => ({ ...p, sequence: [...p.sequence, { step: p.sequence.length + 1, title: '', detail: '', p6Codes: [] }] }))}>+ Step</button>
              </Section>

              <Section title="Logistics">
                <textarea value={body.logistics} onChange={(e) => setBody((p) => ({ ...p, logistics: e.target.value }))} rows={2} style={ta} />
              </Section>
              <Section title="Risks">
                <textarea
                  value={(body.risks || []).join('\n')}
                  onChange={(e) => setBody((p) => ({ ...p, risks: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) }))}
                  rows={3}
                  placeholder="One risk per line"
                  style={ta}
                />
              </Section>
              <Section title="Notes">
                <textarea value={body.notes} onChange={(e) => setBody((p) => ({ ...p, notes: e.target.value }))} rows={2} style={ta} />
              </Section>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
          <Panel title="Documents">
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              <button type="button" style={btn} onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? 'Attaching…' : 'Upload'}
              </button>
              <button type="button" style={btn} onClick={() => setShowSp(true)} disabled={uploading}>From SharePoint</button>
              <input ref={fileRef} type="file" hidden onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ''; }} />
            </div>
            {(plan?.attachments || []).map((a) => (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span title={a.sharepointPath || a.fileName}>
                  {a.source === 'sharepoint' ? '☁ ' : '📎 '}{a.fileName}
                  {a.hasText ? '' : ' · no text'}
                </span>
                <button type="button" style={{ ...btn, padding: '2px 6px', fontSize: 11 }} onClick={() => removeAtt(a.id)}>Remove</button>
              </div>
            ))}
            {!plan?.attachments?.length && <div style={{ fontSize: 12, color: '#94a3b8' }}>Drawings, method statements, lift plans…</div>}
          </Panel>

          <Panel title="P6 hints">
            {!p6?.available && (
              <div style={{ fontSize: 12, color: '#b45309', lineHeight: 1.45 }}>
                <div>{p6?.reason || 'No schedule loaded'}</div>
                <button type="button" style={{ ...btn, marginTop: 8 }} onClick={() => loadP6(rev)}>Retry</button>
              </div>
            )}
            {p6?.available && (
              <div style={{ fontSize: 12, color: '#475569' }}>
                {['crane', 'laydown', 'preassembly', 'sequence'].map((key) => (
                  <div key={key} style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, color: ACCENT, textTransform: 'capitalize' }}>{key}</div>
                    {(buckets[key] || []).slice(0, 5).map((r) => (
                      <div key={r.taskCode} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.taskCode} {r.taskName}
                      </div>
                    ))}
                    {!(buckets[key] || []).length && <div style={{ color: '#94a3b8' }}>none matched</div>}
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel title="Coach" fill>
            <div style={{ flex: 1, overflow: 'auto', fontSize: 13, paddingRight: 4 }}>
              {(plan?.messages || []).map((m) => (
                <div key={m.id} style={{
                  marginBottom: 8, padding: '8px 10px', borderRadius: 8,
                  background: m.role === 'assistant' ? '#f0fdfa' : '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}>
                  <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>
                    {m.role === 'assistant' ? 'Coach' : (m.user_email || 'You')}
                  </div>
                  {m.content}
                </div>
              ))}
              {chatBusy && <div style={{ color: '#64748b', fontSize: 12 }}>Thinking…</div>}
              <div ref={chatEndRef} />
            </div>
            <button
              type="button"
              style={{ ...btnPrimary, width: '100%', margin: '8px 0' }}
              disabled={chatBusy}
              onClick={() => sendChat('Generate the execution one-pager from the attached documents and live P6. Include crane stand, laydown, preassembly areas, and the sequence.')}
            >
              Generate one-pager
            </button>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder="Ask or correct the plan…"
                style={{ ...inp, flex: 1 }}
              />
              <button type="button" style={btnPrimary} disabled={chatBusy || !chatInput.trim()} onClick={() => sendChat()}>Send</button>
            </div>
          </Panel>
        </div>
      </div>

      {showSp && (
        <SharePointBrowser
          jobNumber={jobNumber}
          browseUrl={`${API(jobNumber)}/sharepoint/browse`}
          title={`Attach from SharePoint (${jobNumber})`}
          accept={ATTACH_ACCEPT}
          onPick={pickSharePoint}
          onClose={() => setShowSp(false)}
        />
      )}

      {showEmail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 520, maxWidth: '94vw', padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <strong>Email execution plan</strong>
              <button type="button" style={{ ...btn, border: 'none' }} onClick={() => setShowEmail(false)}>×</button>
            </div>
            <label style={lab}>Subject</label>
            <input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} style={inp} />
            <label style={lab}>To</label>
            <select multiple value={emailTo} onChange={(e) => setEmailTo([...e.target.selectedOptions].map((o) => o.value))} style={{ ...inp, height: 110 }}>
              {members.map((m) => <option key={m.email} value={m.email}>{m.name} ({m.email})</option>)}
            </select>
            <label style={lab}>CC</label>
            <select multiple value={emailCc} onChange={(e) => setEmailCc([...e.target.selectedOptions].map((o) => o.value))} style={{ ...inp, height: 80 }}>
              {members.map((m) => <option key={m.email} value={m.email}>{m.name} ({m.email})</option>)}
            </select>
            <p style={{ fontSize: 12, color: '#64748b' }}>Sends the branded PDF with the job letterhead.</p>
            {emailResult && <p style={{ fontSize: 12, color: '#047857' }}>Sent to {emailResult.sentTo?.join(', ')}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button type="button" style={btn} onClick={() => setShowEmail(false)}>Close</button>
              <button type="button" style={btnPrimary} disabled={emailSending} onClick={sendEmailNow}>
                {emailSending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showHelp && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, width: 720, maxWidth: '94vw', maxHeight: '86vh', overflow: 'auto' }}>
            <div style={{ padding: '12px 16px', background: ACCENT, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Execution Plan help</strong>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <ListenButton compact guideId="execution-plan-help" title="Execution Plan Help" accent="#fff" dark />
                <button type="button" onClick={() => setShowHelp(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
              </div>
            </div>
            <div style={{ padding: 16 }}>
              <HelpWithVideoTour guideId="execution-plan-help" title="Execution Plan Help" accent={ACCENT}>
                <p>This page writes a field one-pager for a job date: where the crane stands, the laydown, preassembly yards, and the sequence.</p>
                <p>It reads live Primavera for this job and lets you attach files from the job SharePoint site or from your computer. The coach uses those documents — it does not invent locations.</p>
                <p>Save a version per date, download the branded PDF, or email it with the correct LGL letterhead for this job number.</p>
                <p>Only Yousef and Behzad can see this menu item.</p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inp = {
  width: '100%',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  padding: '6px 8px',
  fontSize: 13,
  boxSizing: 'border-box',
};
const ta = { ...inp, resize: 'vertical', fontFamily: 'inherit' };
const lab = { display: 'block', fontSize: 12, color: '#64748b', margin: '10px 0 4px' };

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: ACCENT, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function Panel({ title, children, fill }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 12,
      display: 'flex', flexDirection: 'column', minHeight: 0,
      flex: fill ? 1 : undefined,
    }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

export default ExecutionPlan;
